<template>
	<div id="clips_trailers">
		<div class="jw-title-clips">
			<component
				:is="clips.length > 1 ? HiddenHorizontalScrollbar : 'div'"
				@scrollbarClicked="trackScroll"
				:emitOnScroll="true"
				:scrollSnap="true"
				:fillWidth="true"
			>
				<div
					v-for="(clip, index) of visibleClips"
					:key="index"
					class="jw-title-clip"
					@click="getVideoElement"
					:class="{
						'col-sm-6': visibleClips.length > 1,
						'col-sm-12': isActiveClip(clip),
					}"
				>
					<DailyMotionPlayer
						v-if="clip.provider === ClipProvider.Dailymotion"
						class="jw-title-clip-video"
						:clip="clip"
						:options="playerVars"
						:source="source"
						:isActive="isActiveClip(clip)"
						:isTitleTrailer="true"
						@setActiveClipId="onActivate"
						@onBuffering="onTrack('buffering', $event)"
						@onPlaying="onTrack('playing', $event)"
						@onPaused="onTrack('paused', $event)"
						@onEnded="onTrack('ended', $event)"
					>
						<template #image-preview>
							<div
								class="image-preview-container"
								:class="{ 'fallback-img': missingThumbnailIds[clip.externalId] }"
							>
								<img
									:class="{ 'image-error': missingThumbnailIds[clip.externalId] }"
									:src="`https://www.dailymotion.com/thumbnail/video/${clip.externalId}`"
									alt="Trailer Preview Image"
									loading="lazy"
									@error="handleMissingImagePreview(clip.externalId)"
								/>
							</div>
						</template>
					</DailyMotionPlayer>
					<YoutubePlayer
						v-else
						class="jw-title-clip-video"
						:clip="clip"
						:options="playerVars"
						:source="source"
						:isActive="isActiveClip(clip)"
						@setActiveClipId="onActivate"
						@onBuffering="onTrack('buffering', $event)"
						@onPlaying="onTrack('playing', $event)"
						@onPaused="onTrack('paused', $event)"
						@onEnded="onTrack('ended', $event)"
					/>
				</div>
			</component>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick } from 'vue';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClipProvider, ObjectType, SraEventAction } from '@/@types/graphql-types';
import { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import YoutubePlayer from '@/ui-components/YoutubePlayer.vue';
import { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import DailyMotionPlayer from '@/ui-components/players/DailyMotionPlayer.vue';
import { VideoPlayersTrackingIds } from '@/constants/video-players-tracking-ids';

type TitleDetailClip = TitleDetail['content']['clips'][0];
const props = withDefaults(
	defineProps<{
		clips: TitleDetailClip[];
		titleObjectType: ObjectType | null;
		titleObjectId: number | null;
		titleId: string;
		source: string;
		additionalContexts: SnowplowContext[];
		influencedSponsoredAd?: SponsoredAdFragment | undefined;
		activeClipId?: string | null;
		mute?: boolean;
		autoplay?: boolean;
		limit?: number;
		isBlocking?: boolean;
		seasonNumber?: number | null;
		episodeNumber?: number | null;
	}>(),
	{
		limit: 4,
		isBlocking: false,
		mute: false,
		autoplay: false,
		source: '',
		additionalContexts: () => [],
		influencedSponsoredAd: undefined,
		seasonNumber: null,
		episodeNumber: null,
		activeClipId: null,
	}
);

const emit = defineEmits(['setActiveClipId']);

const trackingMap = {
	buffering: 'youtube_buffering',
	playing: 'youtube_playing',
	paused: 'youtube_paused',
	ended: 'youtube_ended',
};

const hasPlayedClips: Record<string, boolean> = {};

const activeVideo = ref<HTMLDivElement | null>(null);

const visibleClips = computed(() => props.clips.slice(0, props.limit));

const hasAutoplay = computed(() => props.limit === 1 && props.autoplay);

const missingThumbnailIds = ref<Record<string, boolean>>({});

const playerVars = computed<YoutubePlayerOptions>(() => ({
	autoplay: hasAutoplay.value ? 1 : 0,
	mute: props.mute ? 1 : 0,
}));

const isActiveClip = (clip: TitleDetailClip) => {
	const id = props.activeClipId?.split(/:(.*)/s); // to split into only array of two strings.
	if (!id) return false;
	return id[0] === props.source && id[1] == clip.externalId;
};

const onActivate = (clipId: string | null) => {
	const id = clipId ? `${props.source}:${clipId}` : null;
	emit('setActiveClipId', id);
};

const { trackSraEvent } = useBackendTracking();

async function onTrack(eventName: keyof typeof trackingMap, clip: TitleDetailClip) {
	const category = trackingMap[eventName];
	if (category) {
		TrackingHelper.trackEvent(
			category,
			{
				action: props.titleObjectType?.toLowerCase(),
				label: props.source,
				nonInteraction: props.autoplay,
				value: VideoPlayersTrackingIds[clip.provider],
			},
			props.additionalContexts
		);
	}

	// track youtube_started only once if the clip is played at least once
	if (eventName === 'playing') {
		await scrollIntoView(activeVideo.value);

		// track influenced campaign metrics
		if (!!props.influencedSponsoredAd?.bidId) {
			trackSraEvent({
				action: SraEventAction.PlayTrailerClicked,
				sponsoredAd: props.influencedSponsoredAd,
				jwEntityID: props.titleId,
			});
		}

		if (!(clip.externalId in hasPlayedClips)) {
			TrackingHelper.trackEvent(
				'youtube_started',
				{
					action: props.titleObjectType?.toLowerCase(),
					label: props.source,
					value: VideoPlayersTrackingIds[clip.provider],
				},
				props.additionalContexts
			);
		}
		hasPlayedClips[clip.externalId] = true;
	}
	// for TrailerAutoplay module
	if (eventName === 'ended') {
		emit('setActiveClipId', null);
	}
}

const trackScroll = (label: string) => {
	const properties: TrackingProviderPropertiesInterface = {
		action: 'horizontal_scroller_clicked',
		property: 'trailers',
		label,
	};
	TrackingHelper.trackEvent('userinteraction', properties, props.additionalContexts);
};

async function scrollIntoView(element: HTMLElement | null) {
	// used in combination with scroll-snap to ensure the active video is always in fully visible
	if (element) {
		// nextTick Ensures the previous active video has time to pause and resize before scrolling
		await nextTick();
		element.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'start',
		});
	}
}

function getVideoElement(event: MouseEvent) {
	activeVideo.value = event.currentTarget as HTMLDivElement;
}

function handleMissingImagePreview(clipId: string) {
	missingThumbnailIds.value = {
		...missingThumbnailIds.value,
		[clipId]: true,
	};
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
.jw-title-clips {
	width: 100%;
	.jw-title-clip {
		min-width: calc(50% - 15px);
		padding-inline-start: 0px;
		scroll-snap-align: center;

		@media (min-width: $screen-sm) {
			scroll-snap-align: start;
		}
		@media (max-width: $screen-sm) {
			width: 320px;
		}

		&-video {
			min-width: 50%;
		}
	}
	&__heading {
		-webkit-font-smoothing: antialiased;
		font-family: Anton, Anton-Fallback, Impact;
		font-size: 22px;
		font-weight: 400;
		line-height: 32px;
		letter-spacing: 0.02em;
		text-transform: uppercase;
		color: #ffffff;
	}
	// adds vertical space between clips
	> .row > [class*='col-'] {
		margin-bottom: calc($grid-gutter-width / 2);
	}
}

.image-preview-container {
	width: 100%;
	aspect-ratio: 16 / 9;
	&.fallback-img {
		background-color: black;
	}

	.image-error {
		opacity: 0;
	}
}
</style>
